import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { FieldExpression, LuceneExpression, NodeExpression, toString as luceneToString } from 'lucene';
import { firstValueFrom, map } from 'rxjs';

import { CLAIM_SERVER_URL } from '../../config';
import { PaginatedResult } from '../../shared/models/paginated-result';
import { OrderBy, OrderByDirection } from '../../shared/utils/constant';
import { QualityIssueStatus } from '../models/all-quality-issue-constants';
import { AllQualityIssue, ALL_ISSUE_FIELDS, FieldOfAllQualityIssue } from '../models/all-quality-issue';
import { padSoldto } from '../../shared/utils/padding';
import { ClaimBatch } from '../models/claim-batch';
import { CURRENCY_OPTIONS, UNIT_OPTIONS } from '../../shared/models/list';

const CLAIM_QUALITYISSUES_V1_URL = `${ CLAIM_SERVER_URL }/v1/allQualityIssues`;

@Injectable({ providedIn: 'root' })
export class AllQualityIssueService implements OnDestroy {
	static readonly ISSUE_TABLE = 'issue';

	claim = new AllQualityIssue();
	newClaim: AllQualityIssue[] = [];
	constructor(
		private readonly http: HttpClient
	) {}

	ngOnDestroy(): void {
		throw new Error('Method not implemented.');
	}

	getRealRemoteIssue(remoteId: string) {
		return this.getQualityIssues(
			ALL_ISSUE_FIELDS,
			this.getAllIssuesbyRemoteIdFilter(remoteId),
			0,
			50,
			[new OrderBy('steeluserId', OrderByDirection.DESC)],
			false
		).pipe(map(res => res.results[0]));
	}

	async saveIssue(claim: AllQualityIssue) {
		const url = new URL(CLAIM_QUALITYISSUES_V1_URL);
		const updateUrl = new URL(`${ CLAIM_QUALITYISSUES_V1_URL }/${ claim.steeluserId }`);
		const headers = new HttpHeaders().set('Content-Type', 'application/json');
		const claimToSend = { ...claim, id: undefined };
		await (
			!claim.steeluserId
				? firstValueFrom(this.http.post<AllQualityIssue>(url.toString(), claimToSend, { headers }))
				: firstValueFrom(this.http.put<AllQualityIssue>(updateUrl.toString(), claimToSend, { headers }))
		);
		// all remaining urls can be revoked once the claim is sent
		for (const attachment of claim.attachments) {
			if (attachment.localUrl) {
				URL.revokeObjectURL(attachment.localUrl);
				attachment.localUrl = undefined;
			}
		}
	}

	async deleteDraft(steeluserId: number) {
		const url = new URL(`${ CLAIM_QUALITYISSUES_V1_URL }/${ steeluserId }`);
		await firstValueFrom(this.http.delete(url.toString()));
	}

	getDownloadFile(steeluserId: number, hash: string, fileName: string) {
		return `${ CLAIM_QUALITYISSUES_V1_URL }/${ steeluserId }/attachments/${ hash }/${ fileName }`;
	}

	getQualityIssues(
		fields?: readonly FieldOfAllQualityIssue[],
		filter?: string,
		index?: number,
		limit?: number,
		orderBy?: OrderBy<keyof AllQualityIssue>[] | OrderBy<keyof AllQualityIssue>,
		distinct?: boolean,
		countOnly?: boolean,
	) {
		const url = new URL(CLAIM_QUALITYISSUES_V1_URL);
		const params = url.searchParams;
		// Fields
		if (fields) {
			params.append('fields', fields.join(','));
		}
		// Filters
		if (filter) {
			params.append('filter', filter);
		}
		// Orderby
		if (orderBy) {
			params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));
		}
		if (index !== undefined && limit !== undefined) {
			params.append('page', String(Math.floor(index / limit) + 1));
		}
		if (limit !== undefined) {
			params.append('limit', String(limit));
		}
		if (distinct) {
			params.append('distinct', String(distinct));
		}
		if (countOnly) {
			params.append('onlyCount', String(countOnly));
		}
		return this.http.get<PaginatedResult<AllQualityIssue>>(url.toString())
			.pipe(map(res => {
				if (Array.isArray(res.results)) {
					for (const result of res.results) {
						if (typeof result.quantityUnitTr === 'string') {
							result.quantityUnitTr =
								<typeof result.quantityUnitTr> result.quantityUnitTr.toLowerCase();
						}
						if (typeof result.amountUnitTr === 'string') {
							result.amountUnitTr =
								<typeof result.amountUnitTr> result.amountUnitTr.toLowerCase();
						}
						if (Array.isArray(result.batches)) {
							for (const batch of result.batches) {
								if (typeof batch.claimedQuantityValue === 'string') {
									batch.claimedQuantityValue = Number(batch.claimedQuantityValue);
								}
								if (typeof batch.claimedQuantityUnitTr === 'string') {
									batch.claimedQuantityUnitTr =
										<typeof batch.claimedQuantityUnitTr> batch.claimedQuantityUnitTr.toLowerCase();
								}
								if (typeof batch.quantityUnitTr === 'string') {
									batch.quantityUnitTr =
										<typeof batch.quantityUnitTr> batch.quantityUnitTr.toLowerCase();
								}
								if (typeof batch.receivedInvoiceQuantityUnitTr === 'string') {
									batch.receivedInvoiceQuantityUnitTr =
										<typeof batch.receivedInvoiceQuantityUnitTr>
										batch.receivedInvoiceQuantityUnitTr.toLowerCase();
								}
								if (typeof batch.receivedInvoiceAmountUnitTr === 'string') {
									batch.receivedInvoiceAmountUnitTr =
										<typeof batch.receivedInvoiceAmountUnitTr>
										batch.receivedInvoiceAmountUnitTr.toLowerCase();
								}
							}
						}
					}
				}
				return res;
			}));
	}

	getQualityIssuesCount(filter?: string) {
		return this.getQualityIssues(
			undefined,
			filter,
			undefined,
			undefined,
			undefined,
			undefined,
			true,
		);
	}

	getClaimBySteeluserId(steeluserId: number) {
		return this.getQualityIssues(
			ALL_ISSUE_FIELDS,
			this.getAllIssuesbyIdFilter(steeluserId),
			0,
			1,
			[new OrderBy('steeluserId', OrderByDirection.DESC)],
			false
		).pipe(map(res => res.results[0]));
	}

	getAllIssuesbyIdFilter(steeluserId: number) {
		const claimIssueIdField: FieldExpression = {
			field: 'steeluserId',
			term: String(steeluserId),
			quoted: true
		};

		return luceneToString(claimIssueIdField);
	}

	getAllIssuesbyRemoteIdFilter(remoteId: string) {
		const claimIssueIdField: FieldExpression = {
			field: 'id',
			term: remoteId,
			quoted: true
		};

		return luceneToString(claimIssueIdField);
	}

	getAllIssuesbyDraftStatusFilter() {
		const claimIssueDraftStatusField: FieldExpression = {
			field: 'statusTr',
			term: QualityIssueStatus.DRAFT,
			quoted: true
		};

		return luceneToString(claimIssueDraftStatusField);
	}

	getAllIssuesbyPendingNotPushedStatusFilter() {
		const claimIssueDraftStatusField: FieldExpression = {
			field: 'statusTr',
			term: QualityIssueStatus.PENDING_NOT_PUSH,
			quoted: true
		};

		return luceneToString(claimIssueDraftStatusField);
	}

	createInListFilter(fieldName: string, values: string[]) {
		let filterCondition: LuceneExpression;
		if (values.length === 1) {
			filterCondition = {
				field: fieldName,
				term: padSoldto(values[0]),
				quoted: true
			};
		} else {
			filterCondition = {
				field: fieldName,
				left: {
					field: '<implicit>',
					term: padSoldto(values[0]),
					quoted: true
				},
				operator: '<implicit>',
				right: {
					field: '<implicit>',
					term: padSoldto(values[1]),
					quoted: true
				},
				parenthesized: true
			};

			let current = filterCondition;
			for (const bocClientTransac of values.slice(2)) {
				current.right = {
					left: current.right,
					operator: '<implicit>',
					right: {
						field: '<implicit>',
						term: padSoldto(bocClientTransac),
						quoted: true
					},
					parenthesized: false
				};
				current = current.right;
			}
		}
		return filterCondition;
	}

	createTransacSoldtoAndShiptoFilter(soldToTransacs: string[], shipToTransacs: string[]) {
		if (soldToTransacs.length !== 0 && shipToTransacs.length !== 0) {
			const transacNode: NodeExpression = {
				left: this.createInListFilter('soldTo', soldToTransacs),
				operator: 'OR',
				parenthesized: true,
				right: this.createInListFilter('shipTo', shipToTransacs)
			};
			return transacNode;
		}
		if (soldToTransacs.length === 0) {
			return this.createInListFilter('shipTo', shipToTransacs);
		}
		return this.createInListFilter('soldTo', soldToTransacs);
	}

	fillClaimFromMainBatch(claim: AllQualityIssue, claimBatch: ClaimBatch) {
		const [mainBatch] = claim.batches;
		if (claimBatch.batchReference !== mainBatch.reference || claimBatch.qtyInvoiced !== mainBatch.quantityValue) {
			claim.batches.splice(1);
		}
		mainBatch.reference = claimBatch.batchReference;
		mainBatch.quantityValue = claimBatch.qtyInvoiced;
		mainBatch.quantityUnitTr =
			<keyof typeof UNIT_OPTIONS>claimBatch.weightUnitInvoiced.toLowerCase();
		mainBatch.receivedOrderSupplierItem = claimBatch.supplierOrderItemNumber;
		mainBatch.receivedOrderSupplierReference = claimBatch.supplierOrderNumber;
		mainBatch.receivedInvoiceQuantityUnitTr =
			<keyof typeof UNIT_OPTIONS>claimBatch.weightUnitInvoiced.toLowerCase();

		// MISSING FIELDS
		mainBatch.receivedInvoiceAmountUnitTr =
			<keyof typeof CURRENCY_OPTIONS | ''>claimBatch.currencyInvoiced.toLowerCase();
		mainBatch.receivedNetQuantityValue = claimBatch.qtyBatchNet;
		mainBatch.soldTo = claimBatch.soldTo;
		mainBatch.sfcC = claimBatch.sfcC;

		const currency = mainBatch.receivedInvoiceAmountUnitTr || 'eur';
		if (claim.amountUnitTr !== currency) {
			claim.amountUnitTr = currency;
			claim.amountValue = 0;
			claim.otherCostAmount = 0;
		}

		if (claim.quantityValue === 0) {
			claim.setQuantityUnitTr(claimBatch.weightUnitInvoiced);
		}
	}

	batchInvoicedOrNot(batchInvoiced: string) {
		return batchInvoiced === 'X' || batchInvoiced === 'Y';
	}
}
